import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Plus, X, Heart, Bell, Circle, List, Edit3, Archive, Link2, Sparkles } from 'lucide-react'
import DemoCursor from './DemoCursor'

// Ministry Map demo — looped walkthrough that mirrors src/pages/MinistryMap.jsx +
// src/components/ministry/*. The map starts WITH two people already on the
// rings so visitors don't see an empty canvas, then the demo:
//   A — Adds Marcus (Close Circle, Neutral)
//   B — Taps Bob → drawer opens in VIEW mode with full detail
//        (traffic light + believer badge + name, relationship + follow-up,
//        Edit, Prayer notes, Current next step, AI Help, Reminder, Archive)
//   C — Clicks Edit → form opens → adds a "Place you overlap" (Gym) +
//        a connection to Sarah ("Met Through") → Save → returns to view
//   D — Sets a 1-week reminder on Bob → bell badge appears on his dot
//   E — Toggles to List view → all four grouped by ring
//   Reset and loop.

// Ring geometry — three concentric circles centered in a 420px canvas.
const CANVAS = 420
const CENTER = CANVAS / 2
const RING_RADIUS = { 1: 65, 2: 130, 3: 188 }
const RING_LABEL = { 1: 'Household', 2: 'Close Circle', 3: 'Sphere of Influence' }

// Spiritual openness — green (Open), yellow (Neutral), red (Closed).
const STATUS_COLOR = { green: '#10b981', yellow: '#f5c844', red: '#dc6c6c' }
const STATUS_LABEL = { green: 'Open', yellow: 'Neutral', red: 'Closed' }

// Relationship → label + ring map (mirrors RELATIONSHIP_LABELS + _RING_MAP
// in src/data/ministryConstants.js). Trimmed to 7 entries so the demo
// edit form fits the drawer width without horizontal pressure.
const RELATIONSHIP_LABEL = {
  family: 'Family',
  close_friend: 'Close Friend',
  small_group: 'Small Group',
  mentor: 'Mentor',
  friend: 'Friend',
  coworker: 'Coworker',
  neighbor: 'Neighbor',
}
const RELATIONSHIP_RING = {
  family: 1,
  close_friend: 2,
  small_group: 2,
  mentor: 2,
  friend: 3,
  coworker: 3,
  neighbor: 3,
}

// Connection type → label (mirrors CONNECTION_TYPE_LABELS).
const CONNECTION_TYPE_LABEL = {
  family: 'Family',
  friend: 'Friend',
  met_through: 'Met Through',
  works_with: 'Works With',
  church_with: 'Church With',
}

// Two pre-loaded people so the demo never opens to an empty canvas. Bob
// carries the full rich detail set so the View drawer in Scene B shows
// everything (prayer notes, next step, follow-up date).
const INITIAL_PEOPLE = [
  {
    id: 'sarah',
    firstName: 'Sarah',
    lastInitial: 'K',
    ring: 1,
    relationshipType: 'family',
    angle: -Math.PI / 2 + Math.PI / 5,
    status: 'green',
    believer: true,
    initials: 'S',
    reminder: null,
    followUp: '4/20/2026',
    prayerNotes: 'For her new role at work — anxiety about leading.',
    nextStep: 'Send the Phil 4 verse this week',
    places: ['Church'],
    connections: [],
  },
  {
    id: 'bob',
    firstName: 'Bob',
    lastInitial: 'J',
    ring: 3,
    relationshipType: 'friend',
    angle: Math.PI / 6,
    status: 'green',
    believer: true,
    initials: 'B',
    reminder: null,
    followUp: '4/18/2026',
    prayerNotes: 'Strength',
    nextStep: 'Ask to disciple',
    places: [],
    connections: [],
  },
]

// Blueprint for the person the demo adds during Scene A.
const MARCUS_BLUEPRINT = {
  id: 'marcus',
  firstName: 'Marcus',
  lastInitial: 'T',
  ring: 2,
  relationshipType: 'close_friend',
  angle: -Math.PI / 2 - Math.PI / 4,
  status: 'yellow',
  believer: false,
  initials: 'M',
  reminder: null,
  followUp: null,
  prayerNotes: '',
  nextStep: '',
  places: [],
  connections: [],
}

function personPosition(person) {
  const r = RING_RADIUS[person.ring]
  return { x: CENTER + r * Math.cos(person.angle), y: CENTER + r * Math.sin(person.angle) }
}

function displayName(p) {
  if (!p) return ''
  return p.lastInitial ? `${p.firstName} ${p.lastInitial}.` : p.firstName
}

// ─── Sub-components ───────────────────────────────────────────────────

function RingCanvas({ people, activePersonId }) {
  // CSS-transform scaling so the 420×420 ring renders at natural size on
  // wide cards (desktop bento) but shrinks proportionally on narrow ones
  // (mobile). ResizeObserver watches the OUTER wrapper; the INNER 420px
  // box gets a `scale(factor)` and the outer height adjusts so layout
  // doesn't reserve extra space. The cursor still tracks correctly
  // because `getBoundingClientRect()` returns post-transform coords.
  const wrapperRef = useRef(null)
  const [scale, setScale] = useState(1)
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    const ro = new ResizeObserver(entries => {
      const w = entries[0].contentRect.width
      setScale(Math.min(1, w / CANVAS))
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return (
    <div
      ref={wrapperRef}
      style={{
        width: '100%',
        maxWidth: CANVAS,
        margin: '0 auto',
        height: CANVAS * scale,
      }}
    >
      <div
        className="relative"
        style={{
          width: CANVAS,
          height: CANVAS,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
        }}
      >
        <svg width={CANVAS} height={CANVAS} viewBox={`0 0 ${CANVAS} ${CANVAS}`}>
          {[3, 2, 1].map(ring => (
            <circle
              key={ring}
              cx={CENTER}
              cy={CENTER}
              r={RING_RADIUS[ring]}
              fill="none"
              stroke="#e9c349"
              strokeWidth="1"
              opacity={ring === 1 ? 0.6 : ring === 2 ? 0.35 : 0.18}
            />
          ))}
          {[1, 2, 3].map(ring => (
            <text
              key={ring}
              x={CENTER}
              y={CENTER - RING_RADIUS[ring] - 6}
              fontSize="9"
              fill="#8891b0"
              textAnchor="middle"
              style={{
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                fontFamily: 'var(--font-label)',
              }}
            >
              {RING_LABEL[ring]}
            </text>
          ))}
          <circle cx={CENTER} cy={CENTER} r="18" fill="#e9c349" />
          <text
            x={CENTER}
            y={CENTER + 4}
            fontSize="13"
            fontWeight="700"
            fill="#3c2f00"
            textAnchor="middle"
            style={{ fontFamily: "'Noto Serif', Georgia, serif" }}
          >
            M
          </text>
        </svg>

        <AnimatePresence>
          {people.map(person => {
            const { x, y } = personPosition(person)
            const isActive = activePersonId === person.id
            return (
              <motion.div
                key={person.id}
                data-cursor-target={`person-${person.id}`}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: isActive ? 1.15 : 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
                className="absolute flex items-center justify-center font-semibold"
                style={{
                  width: 36,
                  height: 36,
                  top: y - 18,
                  left: x - 18,
                  borderRadius: '50%',
                  background: '#0b1229',
                  border: `2px solid ${STATUS_COLOR[person.status]}`,
                  color: '#dce1ff',
                  fontSize: 13,
                  fontFamily: "'Noto Serif', Georgia, serif",
                  boxShadow: isActive
                    ? '0 0 0 4px rgba(233,195,73,0.25), 0 6px 18px rgba(0,0,0,0.5)'
                    : '0 4px 10px rgba(0,0,0,0.4)',
                }}
              >
                {person.initials}
                {person.believer && (
                  <span
                    className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                    style={{ background: '#e9c349' }}
                  >
                    <Heart
                      className="w-2 h-2"
                      style={{ color: '#3c2f00', fill: '#3c2f00' }}
                      aria-hidden="true"
                    />
                  </span>
                )}
                {person.reminder && (
                  <span
                    className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                    style={{ background: '#e9c349' }}
                  >
                    <Bell className="w-2 h-2" style={{ color: '#3c2f00' }} aria-hidden="true" />
                  </span>
                )}
              </motion.div>
            )
          })}
        </AnimatePresence>
      </div>
    </div>
  )
}

function ViewToggle({ view }) {
  return (
    <div className="flex items-center gap-1.5">
      {[
        { id: 'ring', label: 'Rings', Icon: Circle },
        { id: 'list', label: 'List', Icon: List },
      ].map(({ id, label, Icon }) => {
        const active = view === id
        return (
          <div
            key={id}
            data-cursor-target={`view-${id}`}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs font-medium"
            style={{
              background: active ? '#e9c349' : 'rgba(34,41,65,0.5)',
              color: active ? '#3c2f00' : '#8891b0',
              transition: 'all 220ms ease',
            }}
          >
            <Icon className="w-3.5 h-3.5" aria-hidden="true" />
            {label}
          </div>
        )
      })}
    </div>
  )
}

function AddForm({ draftName, draftRing, draftStatus, draftBeliever }) {
  return (
    <>
      <p
        className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
        style={{ color: '#8891b0' }}
      >
        First name
      </p>
      <div
        data-cursor-target="name-input"
        className="w-full rounded-lg px-3 py-2 text-sm mb-4"
        style={{
          background: '#0b1229',
          border: '1px solid rgba(233,195,73,0.3)',
          color: '#dce1ff',
          minHeight: 38,
        }}
      >
        {draftName}
        {draftName && (
          <span
            aria-hidden="true"
            className="inline-block w-[1px] ml-0.5 animate-pulse"
            style={{
              backgroundColor: 'currentColor',
              height: '1em',
              verticalAlign: 'text-bottom',
            }}
          />
        )}
      </div>

      <p
        className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
        style={{ color: '#8891b0' }}
      >
        Ring
      </p>
      <div className="flex flex-col gap-1.5 mb-4">
        {[1, 2, 3].map(r => {
          const active = draftRing === r
          return (
            <div
              key={r}
              data-cursor-target={`ring-${r}`}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm"
              style={{
                background: active ? 'rgba(233,195,73,0.18)' : 'rgba(34,41,65,0.5)',
                border: `1px solid ${active ? 'rgba(233,195,73,0.4)' : 'transparent'}`,
                color: active ? '#e9c349' : '#dce1ff',
              }}
            >
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: '#e9c349', opacity: r === 1 ? 1 : r === 2 ? 0.6 : 0.3 }}
              />
              {RING_LABEL[r]}
            </div>
          )
        })}
      </div>

      <p
        className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
        style={{ color: '#8891b0' }}
      >
        Spiritual openness
      </p>
      <div className="flex gap-1.5 mb-4">
        {['green', 'yellow', 'red'].map(s => {
          const active = draftStatus === s
          return (
            <div
              key={s}
              data-cursor-target={`status-${s}`}
              className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium"
              style={{
                background: active ? `${STATUS_COLOR[s]}22` : 'rgba(34,41,65,0.5)',
                border: `1px solid ${active ? STATUS_COLOR[s] : 'transparent'}`,
                color: active ? STATUS_COLOR[s] : '#8891b0',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS_COLOR[s] }} />
              {STATUS_LABEL[s]}
            </div>
          )
        })}
      </div>

      <div
        data-cursor-target="believer-toggle"
        className="flex items-center justify-between px-3 py-2 rounded-lg"
        style={{
          background: draftBeliever ? 'rgba(233,195,73,0.18)' : 'rgba(34,41,65,0.5)',
          border: `1px solid ${draftBeliever ? 'rgba(233,195,73,0.4)' : 'transparent'}`,
        }}
      >
        <div className="flex items-center gap-2">
          <Heart
            className="w-3.5 h-3.5"
            style={{ color: '#e9c349', fill: draftBeliever ? '#e9c349' : 'none' }}
            aria-hidden="true"
          />
          <span className="text-sm" style={{ color: draftBeliever ? '#e9c349' : '#dce1ff' }}>
            Believer
          </span>
        </div>
        <span
          className="w-7 h-4 rounded-full relative"
          style={{ background: draftBeliever ? '#e9c349' : 'rgba(255,255,255,0.15)' }}
        >
          <span
            className="absolute top-0.5 w-3 h-3 rounded-full bg-white transition-all"
            style={{ left: draftBeliever ? 14 : 2 }}
          />
        </span>
      </div>
    </>
  )
}

function EditForm({
  person,
  editRelationship,
  editPlace,
  editPlaceConfirmed,
  editConnectionPickerOpen,
  editConnectionPerson,
  editConnectionType,
  editConnectionTypePickerOpen,
}) {
  return (
    <>
      {/* Relationship — picker grid, determines the ring */}
      <p
        className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
        style={{ color: '#8891b0' }}
      >
        Relationship
      </p>
      <div className="grid grid-cols-2 gap-1.5 mb-4">
        {Object.entries(RELATIONSHIP_LABEL).map(([value, label]) => {
          const active = editRelationship === value
          return (
            <div
              key={value}
              data-cursor-target={`relationship-${value}`}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium text-center"
              style={{
                background: active ? 'rgba(233,195,73,0.18)' : 'rgba(34,41,65,0.5)',
                border: `1px solid ${active ? 'rgba(233,195,73,0.4)' : 'transparent'}`,
                color: active ? '#e9c349' : '#dce1ff',
              }}
            >
              {label}
            </div>
          )
        })}
      </div>

      {/* Place of overlap */}
      <p
        className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
        style={{ color: '#8891b0' }}
      >
        Place you overlap
      </p>
      <div
        data-cursor-target="place-input"
        className="w-full rounded-lg px-3 py-2 text-sm mb-4 flex items-center gap-2 flex-wrap"
        style={{
          background: '#0b1229',
          border: `1px solid ${editPlaceConfirmed ? 'rgba(94,158,111,0.5)' : 'rgba(233,195,73,0.3)'}`,
          color: '#dce1ff',
          minHeight: 38,
        }}
      >
        {editPlaceConfirmed && editPlace ? (
          <span
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]"
            style={{ background: 'rgba(94,158,111,0.18)', color: '#7ec096' }}
          >
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#7ec096' }} />
            {editPlace}
          </span>
        ) : (
          <>
            <span style={{ color: editPlace ? '#dce1ff' : '#5a6483' }}>
              {editPlace || 'e.g. Office, Church, Gym'}
            </span>
            {editPlace && (
              <span
                aria-hidden="true"
                className="inline-block w-[1px] animate-pulse"
                style={{
                  backgroundColor: 'currentColor',
                  height: '1em',
                  verticalAlign: 'text-bottom',
                }}
              />
            )}
          </>
        )}
      </div>

      {/* Connection — pick a person + connection type */}
      <p
        className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
        style={{ color: '#8891b0' }}
      >
        Connected to
      </p>
      <div className="space-y-1.5 mb-4">
        <div className="relative">
          <div
            data-cursor-target="connection-picker"
            className="w-full rounded-lg px-3 py-2 text-sm flex items-center justify-between"
            style={{
              background: 'rgba(34,41,65,0.5)',
              border: `1px solid ${editConnectionPerson ? 'rgba(233,195,73,0.4)' : 'rgba(255,255,255,0.08)'}`,
              color: editConnectionPerson ? '#e9c349' : '#8891b0',
            }}
          >
            <span>
              {editConnectionPerson ? displayName(editConnectionPerson) : 'Pick a person…'}
            </span>
            <span className="text-xs">▾</span>
          </div>
          {editConnectionPickerOpen && (
            <div
              className="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden"
              style={{
                background: '#1a1f3a',
                border: '1px solid rgba(233,195,73,0.3)',
                zIndex: 5,
                boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
              }}
            >
              {INITIAL_PEOPLE.filter(p => p.id !== person.id).map(p => (
                <div
                  key={p.id}
                  data-cursor-target={`connection-option-${p.id}`}
                  className="px-3 py-2 text-sm flex items-center gap-2"
                  style={{ color: '#dce1ff' }}
                >
                  <span
                    className="w-1.5 h-1.5 rounded-full"
                    style={{ background: STATUS_COLOR[p.status] }}
                  />
                  {displayName(p)}
                </div>
              ))}
            </div>
          )}
        </div>

        {editConnectionPerson && (
          <div className="relative">
            <div
              data-cursor-target="connection-type-picker"
              className="w-full rounded-lg px-3 py-2 text-sm flex items-center justify-between"
              style={{
                background: 'rgba(34,41,65,0.5)',
                border: `1px solid ${editConnectionType ? 'rgba(233,195,73,0.4)' : 'rgba(255,255,255,0.08)'}`,
                color: editConnectionType ? '#e9c349' : '#8891b0',
              }}
            >
              <span>
                {editConnectionType
                  ? CONNECTION_TYPE_LABEL[editConnectionType]
                  : 'How do they know each other?'}
              </span>
              <span className="text-xs">▾</span>
            </div>
            {editConnectionTypePickerOpen && (
              <div
                className="absolute left-0 right-0 top-full mt-1 rounded-lg overflow-hidden"
                style={{
                  background: '#1a1f3a',
                  border: '1px solid rgba(233,195,73,0.3)',
                  zIndex: 5,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.5)',
                }}
              >
                {Object.entries(CONNECTION_TYPE_LABEL).map(([value, label]) => (
                  <div
                    key={value}
                    data-cursor-target={`connection-type-${value}`}
                    className="px-3 py-2 text-sm"
                    style={{ color: '#dce1ff' }}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  )
}

function ViewBody({ person, reminderJustSet }) {
  return (
    <>
      {/* Sub-header — relationship + follow-up + Edit pill */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs" style={{ color: '#8891b0' }}>
          {RELATIONSHIP_LABEL[person.relationshipType] || 'Friend'}
          {person.followUp && <span> · Follow-up {person.followUp}</span>}
        </p>
        <div
          data-cursor-target="edit-button"
          className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-lg"
          style={{ background: 'rgba(233,195,73,0.12)', color: '#e9c349' }}
        >
          <Edit3 className="w-3 h-3" aria-hidden="true" />
          Edit
        </div>
      </div>

      {person.prayerNotes && (
        <section className="mb-4">
          <p
            className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5"
            style={{ color: '#8891b0' }}
          >
            Prayer notes
          </p>
          <p
            className="text-sm px-3 py-2 rounded-lg"
            style={{
              color: '#dce1ff',
              background: 'rgba(20,26,50,0.55)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
          >
            {person.prayerNotes}
          </p>
        </section>
      )}

      {person.nextStep && (
        <section className="mb-4">
          <p
            className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5"
            style={{ color: '#8891b0' }}
          >
            Current next step
          </p>
          <p className="text-sm font-semibold" style={{ color: '#dce1ff' }}>
            {person.nextStep}
          </p>
        </section>
      )}

      {person.places && person.places.length > 0 && (
        <section className="mb-4">
          <p
            className="text-[10px] font-bold uppercase tracking-[0.15em] mb-1.5"
            style={{ color: '#8891b0' }}
          >
            Places
          </p>
          <div className="flex flex-wrap gap-1.5">
            {person.places.map(place => (
              <span
                key={place}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px]"
                style={{ background: 'rgba(94,158,111,0.18)', color: '#7ec096' }}
              >
                <span className="w-1.5 h-1.5 rounded-full" style={{ background: '#7ec096' }} />
                {place}
              </span>
            ))}
          </div>
        </section>
      )}

      <section className="pt-4 mb-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <p
          className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
          style={{ color: '#8891b0' }}
        >
          AI help
        </p>
        <div className="flex gap-1.5">
          <div
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold"
            style={{ background: 'rgba(233,195,73,0.18)', color: '#e9c349' }}
          >
            <Heart className="w-3.5 h-3.5" aria-hidden="true" />
            Generate prayer
          </div>
          <div
            className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium"
            style={{
              background: 'rgba(34,41,65,0.5)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: '#dce1ff',
            }}
          >
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            Suggest next step
          </div>
        </div>
      </section>

      {person.connections && person.connections.length > 0 && (
        <section className="pt-4 mb-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p
            className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2 flex items-center gap-1.5"
            style={{ color: '#8891b0' }}
          >
            <Link2 className="w-3 h-3" aria-hidden="true" />
            Connections
          </p>
          <div className="space-y-1.5">
            {person.connections.map((c, i) => (
              <div
                key={i}
                className="flex items-center justify-between px-3 py-2 rounded-lg"
                style={{
                  background: 'rgba(20,26,50,0.55)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <span className="text-sm" style={{ color: '#dce1ff' }}>
                  <span className="font-semibold">{c.personName}.</span>{' '}
                  <span style={{ color: '#8891b0' }}>{CONNECTION_TYPE_LABEL[c.type]}</span>
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="pt-4 mb-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <p
          className="text-[10px] font-bold uppercase tracking-[0.15em] mb-2"
          style={{ color: '#8891b0' }}
        >
          Pray for
        </p>
        <div
          data-cursor-target="set-reminder"
          className="flex items-center justify-between px-3 py-2.5 rounded-lg"
          style={{
            background: reminderJustSet ? 'rgba(233,195,73,0.18)' : 'rgba(34,41,65,0.5)',
            border: `1px solid ${reminderJustSet ? 'rgba(233,195,73,0.4)' : 'rgba(255,255,255,0.08)'}`,
            color: reminderJustSet ? '#e9c349' : '#dce1ff',
            transition: 'all 250ms ease',
          }}
        >
          <div className="flex items-center gap-2">
            <Bell className="w-3.5 h-3.5" aria-hidden="true" />
            <span className="text-sm">
              {reminderJustSet ? 'Reminder in 1 week' : 'Set reminder'}
            </span>
          </div>
          {reminderJustSet && <span className="text-xs">✓</span>}
        </div>
      </section>

      <section className="pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="flex items-center gap-1.5 text-xs" style={{ color: '#8891b0' }}>
          <Archive className="w-3.5 h-3.5" aria-hidden="true" />
          Archive this person
        </div>
        <p className="text-[10px] mt-1" style={{ color: '#6a7494' }}>
          Archived people are hidden from the map but keep their notes. Restore anytime.
        </p>
      </section>
    </>
  )
}

function PersonDrawer({
  open,
  mode,
  person,
  draftName,
  draftRing,
  draftStatus,
  draftBeliever,
  editRelationship,
  editPlace,
  editPlaceConfirmed,
  editConnectionPickerOpen,
  editConnectionPerson,
  editConnectionType,
  editConnectionTypePickerOpen,
  reminderJustSet,
}) {
  return (
    <AnimatePresence>
      {open && (
        <motion.aside
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.32, ease: [0.16, 1, 0.3, 1] }}
          className="absolute right-0 top-0 bottom-0 flex flex-col"
          style={{
            width: 340,
            background: '#141a32',
            borderLeft: '1px solid rgba(233,195,73,0.18)',
            zIndex: 20,
            boxShadow: '-12px 0 32px rgba(0,0,0,0.4)',
          }}
        >
          {/* Header */}
          <div
            className="flex items-center justify-between px-5 py-3.5 shrink-0"
            style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}
          >
            {mode === 'view' ? (
              <div className="flex items-center gap-2 min-w-0">
                <span
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ background: STATUS_COLOR[person?.status || 'yellow'] }}
                  aria-hidden="true"
                />
                {person?.believer && (
                  <span
                    className="w-4 h-4 rounded-full flex items-center justify-center"
                    style={{ background: '#e9c349' }}
                  >
                    <Heart
                      className="w-2.5 h-2.5"
                      style={{ color: '#3c2f00', fill: '#3c2f00' }}
                      aria-hidden="true"
                    />
                  </span>
                )}
                <p className="text-base font-bold truncate" style={{ color: '#dce1ff' }}>
                  {displayName(person)}
                </p>
              </div>
            ) : (
              <p className="text-sm font-semibold" style={{ color: '#dce1ff' }}>
                {mode === 'add' ? 'Add a person' : 'Edit person'}
              </p>
            )}
            <X className="w-4 h-4 shrink-0" style={{ color: '#8891b0' }} aria-hidden="true" />
          </div>

          <div className="flex-1 overflow-y-auto dark-scrollbar px-5 py-4">
            {mode === 'add' && (
              <AddForm
                draftName={draftName}
                draftRing={draftRing}
                draftStatus={draftStatus}
                draftBeliever={draftBeliever}
              />
            )}
            {mode === 'edit' && person && (
              <EditForm
                person={person}
                editRelationship={editRelationship}
                editPlace={editPlace}
                editPlaceConfirmed={editPlaceConfirmed}
                editConnectionPickerOpen={editConnectionPickerOpen}
                editConnectionPerson={editConnectionPerson}
                editConnectionType={editConnectionType}
                editConnectionTypePickerOpen={editConnectionTypePickerOpen}
              />
            )}
            {mode === 'view' && person && (
              <ViewBody person={person} reminderJustSet={reminderJustSet} />
            )}
          </div>

          {(mode === 'add' || mode === 'edit') && (
            <div
              className="px-5 py-3 shrink-0"
              style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
            >
              <div
                data-cursor-target="drawer-save"
                className="flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold"
                style={{ background: '#e9c349', color: '#3c2f00' }}
              >
                Save
              </div>
            </div>
          )}
        </motion.aside>
      )}
    </AnimatePresence>
  )
}

function ListView({ people }) {
  const ringPeople = ring => people.filter(p => p.ring === ring)
  return (
    <div className="space-y-5 px-2">
      {[1, 2, 3].map(ring => {
        const list = ringPeople(ring)
        if (!list.length) return null
        return (
          <section key={ring}>
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-2 h-2 rounded-full"
                style={{ background: '#e9c349', opacity: ring === 1 ? 1 : ring === 2 ? 0.6 : 0.3 }}
              />
              <h3
                className="text-[11px] font-semibold uppercase tracking-[0.15em]"
                style={{ color: '#8891b0' }}
              >
                {RING_LABEL[ring]}
              </h3>
              <span className="text-[11px]" style={{ color: '#6a7494' }}>
                {list.length}
              </span>
              <div className="flex-1 h-px ml-2" style={{ background: 'rgba(255,255,255,0.06)' }} />
            </div>
            <ul className="space-y-1.5">
              {list.map(p => (
                <li
                  key={p.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg"
                  style={{
                    background: 'rgba(20,26,50,0.55)',
                    border: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <span
                    className="flex items-center justify-center font-semibold shrink-0"
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: '50%',
                      background: '#0b1229',
                      border: `2px solid ${STATUS_COLOR[p.status]}`,
                      color: '#dce1ff',
                      fontSize: 11,
                      fontFamily: "'Noto Serif', Georgia, serif",
                    }}
                  >
                    {p.initials}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm" style={{ color: '#dce1ff' }}>
                      {displayName(p)}
                    </p>
                    <p className="text-[11px]" style={{ color: '#8891b0' }}>
                      <span style={{ color: STATUS_COLOR[p.status] }}>
                        {STATUS_LABEL[p.status]}
                      </span>
                      {p.believer && ' · Believer'}
                      {p.reminder && ' · Reminder set'}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )
      })}
    </div>
  )
}

// ─── Timeline ─────────────────────────────────────────────────────────

function buildTimeline(setters) {
  const {
    setCursor,
    setView,
    setDrawerOpen,
    setDrawerMode,
    setActivePerson,
    setDraftName,
    setDraftRing,
    setDraftStatus,
    setDraftBeliever,
    setEditRelationship,
    setEditPlace,
    setEditPlaceConfirmed,
    setEditConnectionPickerOpen,
    setEditConnectionPerson,
    setEditConnectionType,
    setEditConnectionTypePickerOpen,
    setReminderJustSet,
    commitAddPerson,
    applyPersonEdit,
    setReminderOn,
    reset,
    finish,
    registerInterval,
  } = setters

  const events = []
  let t = 0
  const at = (delay, fn) => {
    t += delay
    events.push({ at: t, fn })
  }

  function typeInto(setter, text, speed = 50) {
    setter('')
    let i = 0
    const id = setInterval(() => {
      i += 1
      setter(text.slice(0, i))
      if (i >= text.length) clearInterval(id)
    }, speed)
    registerInterval(id)
  }

  // Click rhythm: hover (no click) → ring pulse → action.
  function click(target, action, hoverDelay = 500, clickDelay = 400, actionDelay = 450) {
    at(hoverDelay, () => setCursor({ target, click: false }))
    at(clickDelay, () => setCursor({ target, click: true }))
    at(actionDelay, action)
  }

  // ── Scene A: Add Marcus ────────────────────────────────────────────
  click('add-person', () => {
    setDrawerOpen(true)
    setDrawerMode('add')
    setDraftName('')
    setDraftRing(null)
    setDraftStatus(null)
    setDraftBeliever(false)
  })
  click('name-input', () => typeInto(setDraftName, MARCUS_BLUEPRINT.firstName, 60))
  at(MARCUS_BLUEPRINT.firstName.length * 60 + 400, () =>
    setCursor({ target: `ring-${MARCUS_BLUEPRINT.ring}`, click: false })
  )
  at(400, () => setCursor({ target: `ring-${MARCUS_BLUEPRINT.ring}`, click: true }))
  at(450, () => setDraftRing(MARCUS_BLUEPRINT.ring))
  click(`status-${MARCUS_BLUEPRINT.status}`, () => setDraftStatus(MARCUS_BLUEPRINT.status))
  click('drawer-save', () => {
    commitAddPerson(MARCUS_BLUEPRINT)
    setDrawerOpen(false)
  })

  // ── Scene B: Tap Bob → drawer opens in VIEW mode ───────────────────
  click('person-bob', () => {
    setActivePerson('bob')
    setDrawerOpen(true)
    setDrawerMode('view')
    setReminderJustSet(false)
  })
  // Hold so the visitor reads the detail panel.
  at(1800, () => {})

  // ── Scene C: Edit Bob — add a place + a connection to Sarah ────────
  click('edit-button', () => {
    setDrawerMode('edit')
    setEditRelationship('friend')
    setEditPlace('')
    setEditPlaceConfirmed(false)
    setEditConnectionPerson(null)
    setEditConnectionType(null)
  })
  click('place-input', () => typeInto(setEditPlace, 'Gym', 70))
  at('Gym'.length * 70 + 600, () => setEditPlaceConfirmed(true))
  click('connection-picker', () => setEditConnectionPickerOpen(true))
  click('connection-option-sarah', () => {
    setEditConnectionPickerOpen(false)
    setEditConnectionPerson(INITIAL_PEOPLE.find(p => p.id === 'sarah'))
  })
  click('connection-type-picker', () => setEditConnectionTypePickerOpen(true))
  click('connection-type-met_through', () => {
    setEditConnectionTypePickerOpen(false)
    setEditConnectionType('met_through')
  })
  click('drawer-save', () => {
    applyPersonEdit('bob', {
      places: ['Gym'],
      connections: [{ personName: 'Sarah K', type: 'met_through' }],
    })
    setDrawerMode('view')
  })
  // Hold so visitor sees the new Connections + Places sections.
  at(2000, () => {})

  // ── Scene D: Set reminder on Bob ───────────────────────────────────
  click('set-reminder', () => {
    setReminderJustSet(true)
    setReminderOn('bob')
  })
  at(1600, () => {
    setDrawerOpen(false)
    setActivePerson(null)
    setReminderJustSet(false)
  })

  // ── Scene E: Toggle to List view ───────────────────────────────────
  click('view-list', () => setView('list'))
  at(3500, () => {})
  click('view-ring', () => setView('ring'))

  // ── Hold + reset + loop ────────────────────────────────────────────
  at(1800, () => {
    setCursor(null)
    reset()
  })
  at(400, () => finish())

  return events
}

// ─── Main component ───────────────────────────────────────────────────

export default function MinistryMapDemo() {
  const containerRef = useRef(null)
  const [cycleId, setCycleId] = useState(0)

  const [view, setView] = useState('ring')
  const [people, setPeople] = useState(INITIAL_PEOPLE)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerMode, setDrawerMode] = useState('view')
  const [activePersonId, setActivePersonId] = useState(null)

  const [draftName, setDraftName] = useState('')
  const [draftRing, setDraftRing] = useState(null)
  const [draftStatus, setDraftStatus] = useState(null)
  const [draftBeliever, setDraftBeliever] = useState(false)

  const [editRelationship, setEditRelationship] = useState(null)
  const [editPlace, setEditPlace] = useState('')
  const [editPlaceConfirmed, setEditPlaceConfirmed] = useState(false)
  const [editConnectionPickerOpen, setEditConnectionPickerOpen] = useState(false)
  const [editConnectionPerson, setEditConnectionPerson] = useState(null)
  const [editConnectionType, setEditConnectionType] = useState(null)
  const [editConnectionTypePickerOpen, setEditConnectionTypePickerOpen] = useState(false)

  const [reminderJustSet, setReminderJustSet] = useState(false)
  const [cursor, setCursor] = useState(null)

  const intervalsRef = useRef([])

  useEffect(() => {
    intervalsRef.current = []
    const setters = {
      setCursor,
      setView,
      setDrawerOpen,
      setDrawerMode,
      setActivePerson: setActivePersonId,
      setDraftName,
      setDraftRing,
      setDraftStatus,
      setDraftBeliever,
      setEditRelationship,
      setEditPlace,
      setEditPlaceConfirmed,
      setEditConnectionPickerOpen,
      setEditConnectionPerson,
      setEditConnectionType,
      setEditConnectionTypePickerOpen,
      setReminderJustSet,
      commitAddPerson: blueprint =>
        setPeople(prev => (prev.find(p => p.id === blueprint.id) ? prev : [...prev, blueprint])),
      applyPersonEdit: (id, patch) =>
        setPeople(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p))),
      setReminderOn: id =>
        setPeople(prev => prev.map(p => (p.id === id ? { ...p, reminder: '1 week' } : p))),
      registerInterval: id => intervalsRef.current.push(id),
      reset: () => {
        setView('ring')
        setPeople(INITIAL_PEOPLE)
        setDrawerOpen(false)
        setDrawerMode('view')
        setActivePersonId(null)
        setDraftName('')
        setDraftRing(null)
        setDraftStatus(null)
        setDraftBeliever(false)
        setEditRelationship(null)
        setEditPlace('')
        setEditPlaceConfirmed(false)
        setEditConnectionPickerOpen(false)
        setEditConnectionPerson(null)
        setEditConnectionType(null)
        setEditConnectionTypePickerOpen(false)
        setReminderJustSet(false)
      },
      finish: () => setCycleId(c => c + 1),
    }

    const events = buildTimeline(setters)
    const timers = events.map(e => setTimeout(e.fn, e.at))

    return () => {
      timers.forEach(clearTimeout)
      intervalsRef.current.forEach(clearInterval)
      intervalsRef.current = []
    }
  }, [cycleId])

  const activePerson = activePersonId ? people.find(p => p.id === activePersonId) : null

  return (
    <div
      ref={containerRef}
      className="relative overflow-hidden rounded-2xl"
      style={{
        backgroundColor: '#0b1229',
        border: '1px solid rgba(233,195,73,0.15)',
        // Bento-friendly height (matches Chat's 620) so the demo doesn't
        // force the bento grid's rows to grow oversized when embedded
        // in /. Ring canvas + drawer fit at this height.
        height: 620,
      }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 60% at 50% 35%, rgba(233,195,73,0.08) 0%, transparent 70%)',
        }}
      />

      <div className="relative px-6 md:px-8 py-6 md:py-7 max-w-3xl mx-auto h-full flex flex-col">
        <div className="flex items-start justify-between mb-2">
          <div>
            <h1
              className="font-heading text-2xl md:text-3xl font-extrabold tracking-tight"
              style={{ color: '#dce1ff' }}
            >
              Ministry Map
            </h1>
            <p className="mt-1 text-xs italic font-heading" style={{ color: '#8891b0' }}>
              Eyes on the people God put around you.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <ViewToggle view={view} />
            <div
              data-cursor-target="add-person"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ background: '#e9c349', color: '#3c2f00' }}
            >
              <Plus className="w-3.5 h-3.5" aria-hidden="true" />
              Add
            </div>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center mt-2">
          <AnimatePresence mode="wait">
            {view === 'ring' ? (
              <motion.div
                key="rings"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <RingCanvas people={people} activePersonId={activePersonId} />
              </motion.div>
            ) : (
              <motion.div
                key="list"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.25 }}
                className="w-full max-w-md"
              >
                <ListView people={people} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <PersonDrawer
        open={drawerOpen}
        mode={drawerMode}
        person={activePerson}
        draftName={draftName}
        draftRing={draftRing}
        draftStatus={draftStatus}
        draftBeliever={draftBeliever}
        editRelationship={editRelationship}
        editPlace={editPlace}
        editPlaceConfirmed={editPlaceConfirmed}
        editConnectionPickerOpen={editConnectionPickerOpen}
        editConnectionPerson={editConnectionPerson}
        editConnectionType={editConnectionType}
        editConnectionTypePickerOpen={editConnectionTypePickerOpen}
        reminderJustSet={reminderJustSet}
      />

      <DemoCursor cursor={cursor} containerRef={containerRef} />
    </div>
  )
}
